import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import moment from 'moment';
import { get, uniqBy, values, set, cloneDeep } from 'lodash';

import { loadPermitContent } from 'ui-permit/shared-components/slices/permitContent';
import { printBlob, getIssuanceDivision } from 'ui-permit/src/utils/utils';
import { loadContentIntoPermit } from 'ui-permit/src/utils/enrich-backend-data';
import { PERMIT_ENDPOINT } from 'ui-permit/src/constants';

import * as types from '../constants';
import { salesChannelTypes } from '../utils/permitUtils';
import * as enrichment from '../utils/dataUtils';

// Permit Actions
export function prepareIssuance(issuanceObj) {
  const issuance = { ...issuanceObj };
  // ensure start_date is moment and utc offset
  if (issuance.start_date) {
    if (!moment.isMoment(issuance.start_date)) issuance.start_date = moment(issuance.start_date);
    issuance.start_date = issuance.start_date.utcOffset(0).startOf('day').toISOString();
  }

  // ensure start_date is moment and utc offset
  if (issuance.end_date) {
    if (!moment.isMoment(issuance.end_date)) {
      issuance.end_date = moment(issuance.end_date);
    }
    issuance.end_date = issuance.end_date.utcOffset(0).startOf('day').toISOString();
  }
  else {
    issuance.end_date = issuance.start_date;
  }

  if (issuance.lottery_preferences) {
    issuance.lottery_preferences = issuance.lottery_preferences.map((pref) => {
      const newPref = { ...pref };
      if (moment.isMoment(newPref.preferred_date)) {
        const formattedDate = newPref.preferred_date.utcOffset(0).startOf('day').toISOString();
        newPref.preferred_date = formattedDate;
      }
      return newPref;
    });
  }

  issuance.sales_channel_type = salesChannelTypes.get(types.ONLINE);
  return issuance;
}

export function fetchAreasFromPemrit(permit) {
  return (dispatch) => {
    const uniq = uniqBy(values(permit.divisions), 'district');
    const divisions = uniq.map((div) => get(div, 'district'));
    dispatch({
      type: types.SET_DISTRICTS,
      payload: divisions
    });
  };
}

export function fetchPermit(permitId) {
  return (dispatch) => {
    const apiCalls = [
      dispatch(loadPermitContent(permitId)),
      axios.get(PERMIT_ENDPOINT(permitId))
    ];

    return Promise.all(apiCalls)
      .then(([pcRes, pRes]) => {
        const pcPayload = cloneDeep(pcRes.payload);
        const { payload: pPayload } = pRes.data;
        const permit = pRes
          ? loadContentIntoPermit(pPayload, pcPayload)
          : pcPayload;

        const transformedPermit = enrichment.transformPermit(permit);

        dispatch({
          type: types.GET_PERMIT,
          payload: transformedPermit
        });
        dispatch(fetchAreasFromPemrit(transformedPermit));
        return Promise.resolve(transformedPermit);
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_PERMIT_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

export function fetchAggregateSeason(permitId, forLottery = false) {
  return (dispatch) => {
    return axios.get(`${process.env.API}/permits/${permitId}/getAggregateSeason?for_lottery=${forLottery}`)
      .then((success) => {
        dispatch({
          type: types.GET_AGGREGATE_SEASON_RULES,
          payload: success.data.payload
        });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_AGGREGATE_SEASON_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

export function fetchLottery(lotteryId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permits/0/lotteries/${lotteryId}`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({
          type: types.GET_LOTTERY, payload: response.data.payload
        });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_LOTTERY_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

export function fetchCooperators(permitId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permits/${permitId}/cooperators`)
      .then((success) => {
        dispatch({
          type: types.GET_COOPERATORS,
          payload: success.data.payload
        });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_COOPERATORS_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

export function fetchCooperator(permitId, cooperatorId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permits/${permitId}/cooperators/${cooperatorId}`)
      .then((success) => {
        dispatch({
          type: types.GET_COOPERATOR,
          payload: success.data.payload
        });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_COOPERATOR_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

// TODO: if it ever wants to use captcha, needs to get the token from recaptchav3Lib in sarsa
export function postIssuance(issuance, doRedirect, recaptcha = {}) {
  const request = {
    gate_a: recaptcha,
    issuance
  }

  return (dispatch) => axios.post(`${process.env.API}/permits/${issuance.permit_id}/issuances`, JSON.stringify(request),
    { headers: { Authorization: SecurityHelper.getAuthHeader() } })
    .then((response) => {
      dispatch({ type: types.POST_ISSUANCE, payload: response.data.payload });
      if (doRedirect) {
        window.location.assign('/cart');
      }
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        dispatch({
          type: types.POST_ISSUANCE_ERROR,
          payload: 'error while posting issuance data',
          error: error.response.data
        });
      }
      throw error;
    });
}

export function updateIssuanceInProgress() {
  return { type: types.ISSUANCE_UPDATE_INPROGRESS };
}

export function updateIssuance(issuance) {
  const preparedIssuance = prepareIssuance(issuance);
  return (dispatch) => {
    dispatch(updateIssuanceInProgress());
    return axios.post(`${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}`,
      JSON.stringify(preparedIssuance),
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        const updatedIssuance = response.data.payload;
        dispatch({ type: types.UPDATE_ISSUANCE, payload: updatedIssuance });
        return updatedIssuance;
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({ type: types.UPDATE_ISSUANCE_ERROR, payload: 'error while posting issuance data', error: error.response.data });
        }
      });
  }
}

export function cancelIssuance(issuanceId, permitId) {
  // Note: do not actually need the body of the request (right now contains issuanceId)
  return (dispatch) => axios.post(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/cancel`, JSON.stringify(issuanceId),
    { headers: { Authorization: SecurityHelper.getAuthHeader() } })
    .then((response) => {
      dispatch({ type: types.CANCEL_ISSUANCE, payload: response.data.payload });
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        dispatch({
          type: types.CANCEL_ISSUANCE_ERROR,
          payload: 'error while cancelling reservation',
          error: error.response.data
        });
      }
    });
}

export function addItem(itemType, item, doRedirect) {
  const account = SecurityHelper.getAccount()

  const permit_holder = {
    first_name: get(account, 'first_name', ''),
    last_name: get(account, 'last_name', ''),
    middle_name: get(account, 'middle_name', ''),
    email: get(account, 'email', ''),
    phone: get(account, 'cell_phone', '')
  };

  const permit_holder_address = {
    postal_code: get(account, 'home_address.zip_code', ''),
    street_address1: get(account, 'home_address.address1', ''),
    state_code: get(account, 'home_address.state', ''),
    city: get(account, 'home_address.city', '')
  };
  const newItem = { ...item, permit_holder, permit_holder_address };
  const preparedIssuance = prepareIssuance(newItem);
  return postIssuance(preparedIssuance, doRedirect);
}

export async function addItems(items) {
  const issuanceIds = [];
  for (const item of items) {
    const issuance = prepareIssuance(item);
    const response = await axios.post(`${process.env.API}/permits/${issuance.permit_id}/issuances`, JSON.stringify(issuance),
      { headers: { Authorization: SecurityHelper.getAuthHeader() } });
    issuanceIds.push(response.data.payload.id);
  }
  return issuanceIds;
}

export function processCaptchaV3Booking({ doRedirect = false, item }) {
  return (dispatch) =>
    dispatch(addItem(types.PERMIT, item, doRedirect));
}

export function saveFilters(filters) {
  return function (dispatch) {
    return dispatch({
      type: types.SAVE_FILTERS,
      payload: filters
    });
  };
}

export function createLottery(lottery) {
  return (dispatch) => axios.post(`${process.env.API}/permits/${lottery.permit_id}/lotteries`, JSON.stringify(lottery),
    { headers: { Authorization: SecurityHelper.getAuthHeader() } })
    .then((response) => {
      dispatch({ type: types.POST_LOTTERY, payload: response.data.payload });
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        dispatch({
          type: types.POST_LOTTERY_ERROR,
          payload: 'error while creating lottery',
          error: error.response.data
        });
      }
    });
}

export function sendCurrentDate(date) {
  return function (dispatch) {
    return dispatch({
      type: types.SEND_CURRENT_DATE,
      payload: date
    });
  };
}

export function fetchIssuance(issuanceId, permitId) {
  return (dispatch, getState) => {
    return axios.get(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/account`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((success) => {
        const { result } = getState().inyonwhitney.permit;
        dispatch({
          type: types.GET_ISSUANCE,
          payload: { permit: { ...result }, issuance: { ...success.data.payload } }
        });
        return ({ ...success.data.payload })
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_ISSUANCE_ERROR,
            payload: 'error while fetching data',
            error: error.response.data,
            error_code: error.response.status
          });
        }
      });
  };
}

export async function fetchIssuanceAsync(issuanceId, permitId) {
  const response = await axios.get(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/account`,
    { headers: { Authorization: SecurityHelper.getAuthHeader() } });
  return response.data.payload;
}

export function fetchRecarea(recareaId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/search?fq=entity_type%3Arecarea&fq=entity_id%3A${recareaId}`)
      .then((success) => {
        if (success.data && success.data.size > 0) {
          dispatch({
            type: types.GET_RECAREA,
            payload: Object.assign(success.data.results)
          });
        }
        else {
          const error = 'No recreation area found';
          dispatch({ type: types.GET_RECAREA_ERROR, payload: error, error });
        }
      }
      )
      .catch((error) => {
        dispatch({ type: types.GET_RECAREA_ERROR, payload: 'Error while fetching recarea data', error });
      });
  };
}

export function fetchCart() {
  return function (dispatch) {
    return axios.get(`${process.env.API}/cart/shoppingcart`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({ type: types.GET_CART, payload: response.data });
      })
      .catch(() => {
        dispatch({ type: types.GET_CART_ERROR, payload: 'error fetching cart', error: true });
      });
  };
}

export function cancelReservation(reservationId, history) {
  return function (dispatch) {
    return axios.delete(`${process.env.API}/cart/shoppingcart/${reservationId}`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({ type: types.CANCEL_RESERVATION, payload: response.data });
        dispatch(fetchCart());
        history.goBack();
      })
      .catch(() => {
        dispatch({ type: types.CANCEL_RESERVATION_ERROR, payload: 'error canceling reservation', error: true });
      });
  };
}

export function fetchLotteryIssuances(lotteryId, statuses, alias) {
  const item = {
    lottery_id: lotteryId,
    statuses
  };
  return function (dispatch) {
    return axios.post(`${process.env.API}/permits/0/lotteries/issuances`, JSON.stringify(item),
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({ type: types.GET_ISSUANCE_BY_STATUS, payload: { [alias]: response.data.payload } });
      })
      .catch(() => {
        dispatch({
          type: types.GET_ISSUANCE_BY_STATUS_ERROR,
          payload: 'error fetching issuances',
          error: true
        });
      });
  };
}

export function notifyLotteryResult(lotteryId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permits/lotteries/${lotteryId}`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then(() => {
        dispatch({ type: types.NOTIFY_LOTTERY_RESULTS, payload: 'Notify Lottery Results Success' });
      })
      .catch((error) => {
        dispatch({
          type: types.NOTIFY_LOTTERY_RESULTS_ERROR,
          payload: 'error notify lottery results',
          error: error.response.data
        });
      });
  };
}

export function printIssuancePDF(permitId, issuanceId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/pdf`,
      {
        headers: { Authorization: SecurityHelper.getAuthHeader() },
        responseType: 'arraybuffer'
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        printBlob(blob);
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_ISSUANCE_PDF_ERROR,
            payload: 'error printing issuance PDF',
            error: error.response.data
          });
        }
      });
  };
}

export function getLottery(lotteryId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/lottery/${lotteryId}`)
      .then((response) => {
        dispatch({ type: types.GET_LOTTERY, payload: response.data.lottery });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({
            type: types.GET_LOTTERY_ERROR,
            payload: 'error while fetching data',
            error: error.response.data
          });
        }
      });
  };
}

export function fetchLotteries(permitId) {
  return (dispatch) => {
    return axios.get(`${process.env.API}/lottery/available`)
      .then((response) => {
        const lotteriesForPermit = get(response.data, 'lotteries', [])
          .filter((lottery) => lottery.inventory_type === 'permit' && lottery.inventory_id === permitId);

        dispatch({ type: types.GET_LOTTERIES, payload: lotteriesForPermit });
      })
      .catch((error) => {
        // if there is an error on the payload (ie: from lottery service), use it. Otherwise, use the top level error
        const generalMessage = get(error, "response.data.error");
        const message = get(error, "response.data.payload.error", generalMessage);
        dispatch({ type: types.GET_LOTTERIES_ERROR, payload: 'error while fetching lotteries', error: message });
      });
  };
}

export function fetchCampsites(permitId) {
  return function (dispatch) {
    return axios.get(`${process.env.API}/permitcontent/${permitId}/campsites`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({ type: types.GET_CAMPSITES, payload: response.data.payload });
      })
      .catch((error) => {
        dispatch({
          type: types.GET_CAMPSITES_ERROR,
          payload: 'error fetch campsites',
          error: error.response.data
        });
      });
  };
}

export function calculateFeeDifference(issuance) {
  const preparedIssuance = prepareIssuance(issuance);

  return (dispatch) => axios.post(`${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/fees`, JSON.stringify(preparedIssuance),
    { headers: { Authorization: SecurityHelper.getAuthHeader() } })
    .then((response) => {
      dispatch({ type: types.POST_ISSUANCE_CALCULATE_FEES, payload: response.data.payload });
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        dispatch({
          type: types.POST_ISSUANCE_CALCULATE_FEES_ERROR,
          payload: 'error while calculating fee difference',
          error: error.response.data
        });
      }
    });
}

export function postIssuanceCartless(issuance) {
  const preparedIssuance = prepareIssuance(issuance);

  return (dispatch) => axios.post(`${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/cartless`, JSON.stringify(preparedIssuance),
    { headers: { Authorization: SecurityHelper.getAuthHeader() } })
    .then((response) => {
      dispatch({ type: types.POST_ISSUANCE_CARTLESS, payload: response.data.payload });
    })
    .catch((error) => {
      if (error.response && error.response.data) {
        dispatch({
          type: types.POST_ISSUANCE_CARTLESS_ERROR,
          payload: 'error while posting issuance without payment',
          error: error.response.data
        });
      }
    });
}

export function bypassToOrderConfirmation(issuance, permit) {
  const division = getIssuanceDivision(issuance, permit);
  const payload = {
    order_id: issuance.order_id,
    completed_reservations: [{
      order_details: {
        description: permit.name,
        img_url: permit.img_url,
        location: division ? division.name : ''
      },
      order_id: issuance.order_id
    }],
    subtotal: 0,
    tax: 0,
    total: 0
  };

  return (dispatch) => {
    dispatch({ type: types.FINALIZE_CHECKOUT, payload });
  };
}


export function fetchDivisionCampsites(permitId, divisionId) {
  return (dispatch) => {
    const url = `${process.env.API}/permitcontent/${permitId}/divisions/${divisionId}/campsites`;
    return axios.get(url,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((response) => {
        dispatch({ type: types.GET_DIVISION_CAMPSITES, payload: response.data.payload });
      })
      .catch((error) => {
        dispatch({
          type: types.GET_DIVISION_CAMPSITES_ERROR,
          payload: 'error fetch campsites',
          error: error.response.data
        });
      });
  };
}

export const replacePendingReservation = (newObj) => ({ type: types.INPROGRESS_ISSUANCE_UPDATED, payload: newObj });

export const updatePendingReservation = (id, path, value) => {
  return (dispatch, getState) => {
    const { issuance } = getState().inyonwhitney.permit;
    const updatingObj = { ...issuance };
    const newObj = set(updatingObj, path, value);
    dispatch(replacePendingReservation(newObj));
  };
};

export function formValidationChange(path, error) {
  return (dispatch) => {
    dispatch({
      type: types.SET_VALIDATION_ERROR,
      payload: { path, error }
    });
  };
}

export const redirectToAvailabilityPage = (reservation, history) => {
  return () => {
    const reservationId = `${reservation.id}${types.RES_ID_DELIMITER}${reservation.permit_id}`;
    let url = `/permits/${reservation.permit_id}/registration/detailed-availability`;

    const query = { reservationId, date: moment.utc(reservation.start_date).toJSON() };
    // TODO use encodeQueryParams
    const queryString = Object.keys(query)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
      .join('&');
    if (queryString) {
      url = `${url}?${queryString}`;
    }
    history.push(url);
  }
}

export function clearIssuanceFromStore() {
  return (dispatch) => {
    dispatch({ type: types.CLEAR_ISSUANCE });
  };
}

export function clearFetchIssuanceError() {
  return (dispatch) => {
    dispatch({ type: types.CLEAR_IW_FETCH_ISSUANCE_ERROR });
  };
}

export const showLoginModal = () => {
  return (dispatch) => {
    dispatch({ type: types.SHOW_LOGIN_MODAL });
  };
};

export function setPermitType(value) {
  return {
    type: types.SET_PERMIT_TYPE,
    permitType: value
  };
}

export function setPermitDate(value) {
  return {
    type: types.SET_PERMIT_DATE,
    permitDate: value
  };
}

export function setSelectedDivision(division) {
  return {
    type: types.SET_SELECTED_DIVISION,
    division
  };
}

export function setSelectedStartDate(value) {
  return {
    type: types.SET_SELECTED_START_DATE,
    date: value
  };
}

export function setSelectedEndDate(value) {
  return {
    type: types.SET_SELECTED_END_DATE,
    date: value
  };
}

export function setSelectedDivision2(division) {
  return {
    type: types.SET_SELECTED_DIVISION2,
    division
  };
}

export function setSelectedStartDate2(value) {
  return {
    type: types.SET_SELECTED_START_DATE2,
    date: value
  };
}

export function setSelectedEndDate2(value) {
  return {
    type: types.SET_SELECTED_END_DATE2,
    date: value
  };
}

export function setQuestionnaireAnswers(answers) {
  return {
    type: types.SET_QUESTIONNAIRE_ANSWERS,
    answers
  };
}

export function setActiveTab(value) {
  return {
    type: types.SET_ACTIVE_TAB,
    activeTab: value
  };
}

export function setDivisionSearchCriteria(criteria) {
  return {
    type: types.SET_DIV_SEARCH_CRITERIA,
    divisionSearchCriteria: criteria
  };
}

export function setStayOvernightCampsite(date, name) {
  return {
    type: types.SET_OVERNIGHT_CAMPSITE,
    payload: { date, name }
  };
}

export function deleteOvernightCampsite(date) {
  return (dispatch) => dispatch({
    type: types.UNSET_OVERNIGHT_CAMPSITE,
    payload: { date }
  });
}

export function clearOvernightCampsite() {
  return (dispatch) => dispatch({
    type: types.CLEAR_OVERNIGHT_CAMPSITE
  });
}

export function setDisplayValidationErros() {
  return (dispatch) => dispatch({
    type: types.DISPLAY_UI_VALIDATIONS
  });
}

export function fetchUnpaidIssuance(issuanceId, permitId) {
  return (dispatch, getState) => {
    return axios.get(`${process.env.API}/permits/${permitId}/issuances/${issuanceId}/unpaid`,
      { headers: { Authorization: SecurityHelper.getAuthHeader() } })
      .then((success) => {
        const { result } = getState().inyonwhitney.permit;
        dispatch({
          type: types.GET_ISSUANCE,
          payload: { permit: { ...result }, issuance: { ...success.data.payload } }
        });
        return ({ ...success.data.payload })
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          dispatch({ type: types.GET_ISSUANCE_ERROR, payload: 'error while fetching data', error: error.response.data, error_code: error.response.status });
          return (error.response.data);
        }
        return error;
      });
  };
}

export function fetchAccountEmployees() {
  return async (dispatch) => {
    try {
      const url = `${process.env.API}/permits/account/employees`;
      const result = await axios.get(url, { headers: { Authorization: SecurityHelper.getAuthHeader() } });
      dispatch({ type: types.GET_ACCOUNT_EMPLOYEES, employees: result.data.payload });
    }
    catch (error) {
      dispatch({ type: types.GET_ACCOUNT_EMPLOYEES_ERROR, error: error.response.data.error });
    }
  };
}
